import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
}

/**
 * Error Boundary qui détecte les erreurs de chargement de chunks (modules dynamiques)
 * et force un refresh automatique de la page.
 *
 * Ce problème survient quand un nouveau déploiement change les hashes des fichiers JS
 * et qu'un utilisateur a encore une ancienne version en cache.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    // Détecter les erreurs de chargement de chunks
    if (ChunkErrorBoundary.isChunkLoadError(error)) {
      return { hasChunkError: true };
    }
    return null;
  }

  static isChunkLoadError(error: Error): boolean {
    const message = error.message || "";
    const name = error.name || "";

    // Patterns d'erreurs de chargement de chunks
    return (
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Loading chunk") ||
      message.includes("Loading CSS chunk") ||
      message.includes("ChunkLoadError") ||
      name === "ChunkLoadError" ||
      // Erreur MIME type quand le serveur renvoie du HTML au lieu du JS
      message.includes("MIME type")
    );
  }

  componentDidCatch(error: Error) {
    if (ChunkErrorBoundary.isChunkLoadError(error)) {
      // Éviter les boucles infinies de refresh
      const lastRefresh = sessionStorage.getItem("chunk_error_refresh");
      const now = Date.now();

      if (lastRefresh && now - parseInt(lastRefresh) < 10000) {
        // Si un refresh a eu lieu il y a moins de 10 secondes, ne pas re-refresh
        console.error("Chunk load error detected but refresh already attempted recently");
        return;
      }

      // Marquer le timestamp du refresh
      sessionStorage.setItem("chunk_error_refresh", now.toString());

      // Force un hard refresh pour vider le cache
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasChunkError) {
      // Ce rendu ne devrait jamais être visible car on fait un reload avant
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-muted-foreground">Mise à jour en cours...</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
